import { useMemo } from 'react';
import type { CellChange } from '@silevis/reactgrid';
import {
  combineMessage,
  getCellWidths,
  getColumnByType,
  getRowHeight,
  updateDataByType
} from '../utils/functions';
import commonStore from '../../../../store/common';
import { CustomHeaderWithDrillDown } from '../../components/CustomHeaderWithDrillDown';
import columnToLetter from '../../../../utils/columnToLetter';
import { useSearchParams } from 'react-router-dom';
import { reaction } from 'mobx';
import reportStore from '../../store';
import { useSyncExternalStore } from 'react';
import type {
  FundDataRow,
  FundEntry,
  ReportGridColumn,
  ReportGridRow,
  SchemaField,
  UseTableBaseProps
} from './types';
import { isFcaTableDisabledForFund } from '../utils/visibleSections';
import { deepCopy } from '../../../../utils/deepCopy';

const columnLables = {
  africa: {
    type: 'number',
    name: 'Africa',
    order: 47
  },
  asia_pacific: {
    type: 'number',
    name: 'Asia and Pacific (other than Middle East)',
    order: 48
  },
  uk: {
    type: 'number',
    name: 'UK',
    order: 250
  },
  europe_non_uk: {
    type: 'number',
    name: 'Europe (other than UK)',
    order: 251
  },
  middle_east: {
    type: 'number',
    name: 'Middle East',
    order: 51
  },
  north_america: {
    type: 'number',
    name: 'North America',
    order: 52
  },
  south_america: {
    type: 'number',
    name: 'South America',
    order: 53
  },
  supra_national: {
    type: 'number',
    name: 'Supranational / multiple region',
    order: 54
  }
};

const _tables = [
  { title: '% of aggregated asset value', key: 'aum_rate' },
  { title: '% of NAV', key: 'nav_rate' }
];

interface GeographicalFocusSchemaType {
  type: string;
  options?: unknown[];
  has_drill_down_feature?: boolean;
}

export type GeographicalFocusSchema = SchemaField & {
  types: Record<string, GeographicalFocusSchemaType>;
  defaultValues: Record<string, unknown>;
};

interface GeographicalFocusDifferenceCell {
  is_changed?: boolean;
  change_percent?: number;
  change_direction?: 'up' | 'down' | string;
  [key: string]: unknown;
}

type GeographicalFocusDifferenceTable = Record<
  string,
  Array<Record<string, GeographicalFocusDifferenceCell>>
>;

interface GeographicalFocusRow extends FundDataRow {
  fund_id?: string;
  fund_name?: string;
  period?: string;
  year?: string;
  report_id?: string | number;
  id?: string | number;
  errors?: Record<string, { msg?: string } | undefined>;
  warning?: Record<string, { msg?: string } | undefined>;
}

export interface GeographicalFocusFund extends FundEntry {
  fund_data: Record<string, GeographicalFocusRow[]>;
  previous_fund?: GeographicalFocusFund;
  period?: string;
  year?: string;
  difference?: GeographicalFocusDifferenceTable;
}

type GeographicalFocusCellChange = CellChange & {
  newCell: {
    dataIndex: number;
    dataKey: string;
    fundId?: string;
    value?: unknown;
  };
};

interface UseGeographicalFocusProps extends Omit<UseTableBaseProps, 'data' | 'schema' | 'funds'> {
  data?: GeographicalFocusRow[] | null;
  schema: GeographicalFocusSchema;
  funds: Record<string, GeographicalFocusFund>;
  tableName?: never;
  columnLables?: never;
}

interface UseGeographicalFocusReturn {
  tables: ReportGridRow[][];
  columns: ReportGridColumn[];
  onChange: (changes: GeographicalFocusCellChange[]) => void;
}

interface RowHeightCell {
  value: string | number;
  error: string;
}

export default function useGeographicalFocus({
  data,
  onChangeFund,
  schema,
  isStaticTemplate,
  requiredFields,
  funds,
  isMultiFunds
}: UseGeographicalFocusProps): UseGeographicalFocusReturn {
  const [searchParams] = useSearchParams();
  const currentDataKey = searchParams.get('fund_id')!;
  const isTableDisabledForFund = fundOrId => {
    const fund =
      typeof fundOrId === 'object' && fundOrId !== null
        ? fundOrId
        : funds[String(fundOrId)] || funds[fundOrId];
    return isFcaTableDisabledForFund(fund, 'GEOGRAPHICAL_FOCUS');
  };
  const isComparisonReport = useSyncExternalStore(
    subscribe => {
      const dispose = reaction(() => reportStore.isComparisonReport, subscribe, {
        fireImmediately: false
      });
      return dispose;
    },
    () => reportStore.isComparisonReport
  );
  const contentWidth = useSyncExternalStore(
    subscribe => reaction(() => commonStore.contentWidth, subscribe, { fireImmediately: false }),
    () => commonStore.contentWidth
  );
  const onChange = (value: GeographicalFocusCellChange[]) => {
    const changes = value[0];
    const fund = isMultiFunds
      ? deepCopy(funds[changes.newCell.fundId!].fund_data['GEOGRAPHICAL_FOCUS'])
      : deepCopy(data);

    fund[changes.newCell.dataIndex][changes.newCell.dataKey] = updateDataByType({
      cell: changes.newCell
    });

    onChangeFund('GEOGRAPHICAL_FOCUS', fund, changes.newCell.fundId);
  };

  const columns = useMemo<ReportGridColumn[]>(() => {
    let _columns: ReportGridColumn[] = [];
    if (!isMultiFunds) {
      _columns = Array.from(Array(isComparisonReport ? 3 : 2).keys()).map(index => ({
        columnId: index + 2,
        width: contentWidth / (isComparisonReport ? 3 : 2)
      }));
    } else {
      const _fundSize = Object.keys(funds).length;
      _columns =
        _fundSize <= 3 && !isComparisonReport
          ? Array.from(Array(_fundSize + 1).keys()).map(index => ({
              columnId: index + 2,
              width: contentWidth / (_fundSize + 1)
            }))
          : isComparisonReport
            ? [
                {
                  columnId: 2,
                  width: 200
                },
                ...(Object.values(funds) as GeographicalFocusFund[])
                  .map(item => [item, item.previous_fund])
                  .flat()
                  .map((_, index) => ({
                    columnId: index + 3,
                    width: 300
                  }))
              ]
            : [
                {
                  columnId: 2,
                  width: 200
                },
                ...Object.keys(funds).map((_, index) => ({
                  columnId: index + 3,
                  width: 300
                }))
              ];
    }
    return [
      {
        columnId: 0,
        resizable: true,
        width: 30
      },
      {
        columnId: 1,
        resizable: true,
        width: 30
      },
      ..._columns
    ];
  }, [isMultiFunds, funds, contentWidth, isComparisonReport]);

  const tables = useMemo<ReportGridRow[][]>(() => {
    if (!data) return [];

    const { labelWidth, valueWidth } = getCellWidths({
      isMultiFunds,
      isComparisonReport,
      fundSize: Object.keys(funds).length,
      contentWidth
    });

    return _tables.map((value, ind) => {
      let _rows: ReportGridRow[] = [];
      if (!isMultiFunds) {
        _rows = [
          {
            rowId: 0,
            height: 40,
            cells: [
              {
                type: 'header',
                text: ''
              },
              {
                type: 'header',
                text: ''
              },
              {
                type: 'header',
                text: value.title
              },
              ...(isComparisonReport
                ? [
                    {
                      type: 'header',
                      text: `${funds[currentDataKey].previous_fund?.period}, ${funds[currentDataKey].previous_fund?.year}`
                    },
                    {
                      type: 'header',
                      text: `${funds[currentDataKey].period}, ${funds[currentDataKey].year}`
                    }
                  ]
                : [
                    {
                      type: 'header',
                      text: ''
                    }
                  ])
            ]
          }
        ];
      } else {
        _rows = [
          {
            rowId: 0,
            height: 40,
            cells: [
              {
                type: 'header',
                text: ''
              },
              {
                type: 'header',
                text: ''
              },
              {
                type: 'header',
                text: value.title
              },
              ...(isComparisonReport
                ? Object.values(funds)
                    .map(item => [item, item.previous_fund])
                    .flat()
                    .map((value, j) => ({
                      type: 'header',
                      text: value?.fund_name || '',
                      colspan: j % 2 === 0 ? 2 : undefined
                    }))
                : Object.values(funds).map(value => ({
                    type: 'header',
                    text: value?.fund_name || ''
                  })))
            ]
          },
          ...(isComparisonReport && isMultiFunds
            ? [
                {
                  rowId: 1,
                  height: 40,
                  cells: [
                    {
                      type: 'header',
                      text: ''
                    },
                    {
                      type: 'header',
                      text: ''
                    },
                    {
                      type: 'header',
                      text: value.title
                    },
                    ...Object.values(funds)
                      .map(item => [item.previous_fund, item])
                      .flat()
                      .map(value => ({
                        type: 'header',
                        text: `${value?.period}, ${value?.year}`
                      }))
                  ]
                }
              ]
            : [])
        ];
      }
      return [
        ..._rows,
        ...(Object.keys(columnLables) as Array<keyof typeof columnLables>).map((key, index) => {
          const fieldKey = `${key}_${value.key}`;
          const isRequired = requiredFields.includes(fieldKey);
          let cells: Array<ReturnType<typeof getColumnByType>> = [];
          if (isMultiFunds) {
            cells = isComparisonReport
              ? (
                  Object.values(funds)
                    .map(item => [item.previous_fund, item])
                    .flat() as GeographicalFocusFund[]
                ).map((item, j) =>
                  getColumnByType({
                    type: schema.types[`${key}_${value.key}`]?.type,
                    options: schema.types[`${key}_${value.key}`].options,
                    key: `${key}_${value.key}`,
                    defaultValue: schema.defaultValues[`${key}_${value.key}`],
                    index: 0,
                    value: item.fund_data.GEOGRAPHICAL_FOCUS[0],
                    required: requiredFields.includes(`${key}_${value.key}`),
                    tableName: 'GEOGRAPHICAL_FOCUS',
                    isStaticTemplate,
                    fundId: item.fund_id,
                    isSectionDisabled: isTableDisabledForFund(item.fund_id),
                    fundName: item.fund_name,
                    hasDrillDownFeature:
                      !!item.fund_data['GEOGRAPHICAL_FOCUS'][0]?.report_id &&
                      item.fund_data['GEOGRAPHICAL_FOCUS'][0]?.[`${key}_${value.key}`] !== null &&
                      schema.types[`${key}_${value.key}`].has_drill_down_feature,
                    reportId: item.fund_data['GEOGRAPHICAL_FOCUS'][0]?.report_id,
                    tableId: item.fund_data['GEOGRAPHICAL_FOCUS']?.[0]?.id,
                    title: `${columnLables[key].name} ${value.title}`,
                    isMultiFunds,
                    fieldOrder: columnLables[key]?.order,
                    difference:
                      j % 2 === 1
                        ? funds[item.fund_id!]?.difference?.['GEOGRAPHICAL_FOCUS']?.[0]?.[
                            `${key}_${value.key}`
                          ] || {}
                        : null,
                    nonEditable: isComparisonReport
                  })
                )
              : Object.values(funds).map(item =>
                  getColumnByType({
                    type: schema.types[`${key}_${value.key}`].type,
                    options: schema.types[`${key}_${value.key}`].options,
                    key: `${key}_${value.key}`,
                    defaultValue: schema.defaultValues[`${key}_${value.key}`],
                    index: 0,
                    value: item.fund_data.GEOGRAPHICAL_FOCUS[0],
                    required: requiredFields.includes(`${key}_${value.key}`),
                    tableName: 'GEOGRAPHICAL_FOCUS',
                    isStaticTemplate,
                    fundId: item.fund_id,
                    isSectionDisabled: isTableDisabledForFund(item.fund_id),
                    fundName: item.fund_name,
                    hasDrillDownFeature:
                      !!item.fund_data['GEOGRAPHICAL_FOCUS'][0]?.report_id &&
                      item.fund_data['GEOGRAPHICAL_FOCUS'][0]?.[`${key}_${value.key}`] !== null &&
                      schema.types[`${key}_${value.key}`].has_drill_down_feature,
                    reportId: item.fund_data['GEOGRAPHICAL_FOCUS'][0]?.report_id,
                    tableId: item.fund_data['GEOGRAPHICAL_FOCUS']?.[0]?.id,
                    title: `${columnLables[key].name} ${value.title}`,
                    isMultiFunds,
                    fieldOrder: columnLables[key]?.order,
                    nonEditable: isComparisonReport
                  })
                );
          } else {
            cells = isComparisonReport
              ? [
                  getColumnByType({
                    type: schema.types[`${key}_${value.key}`].type,
                    options: schema.types[`${key}_${value.key}`].options,
                    key: `${key}_${value.key}`,
                    defaultValue: schema.defaultValues[`${key}_${value.key}`],
                    index: 0,
                    value: funds[currentDataKey].previous_fund!.fund_data['GEOGRAPHICAL_FOCUS'][0],
                    required: requiredFields.includes(`${key}_${value.key}`),
                    tableName: 'GEOGRAPHICAL_FOCUS',
                    isStaticTemplate,
                    isMultiFunds,
                    fundId: funds[currentDataKey].previous_fund!.fund_id,
                    isSectionDisabled: isTableDisabledForFund(funds[currentDataKey].previous_fund),
                    fundName: funds[currentDataKey].previous_fund!.fund_name,
                    fieldOrder: columnLables[key]?.order,
                    nonEditable: isComparisonReport
                  }),
                  getColumnByType({
                    type: schema.types[`${key}_${value.key}`].type,
                    options: schema.types[`${key}_${value.key}`].options,
                    key: `${key}_${value.key}`,
                    defaultValue: schema.defaultValues[`${key}_${value.key}`],
                    index: 0,
                    value: data[0],
                    required: requiredFields.includes(`${key}_${value.key}`),
                    tableName: 'GEOGRAPHICAL_FOCUS',
                    isStaticTemplate,
                    isMultiFunds,
                    fundId: data[0]?.fund_id,
                    isSectionDisabled: isTableDisabledForFund(data[0]?.fund_id),
                    fundName: data[0].fund_name,
                    fieldOrder: columnLables[key]?.order,
                    difference:
                      funds[currentDataKey].difference?.['GEOGRAPHICAL_FOCUS']?.[0]?.[
                        `${key}_${value.key}`
                      ] || {},
                    nonEditable: isComparisonReport
                  })
                ]
              : [
                  getColumnByType({
                    type: schema.types[`${key}_${value.key}`].type,
                    options: schema.types[`${key}_${value.key}`].options,
                    key: `${key}_${value.key}`,
                    defaultValue: schema.defaultValues[`${key}_${value.key}`],
                    index: 0,
                    value: data[0],
                    required: requiredFields.includes(`${key}_${value.key}`),
                    tableName: 'GEOGRAPHICAL_FOCUS',
                    isStaticTemplate,
                    isMultiFunds,
                    fundId: data[0]?.fund_id,
                    fundName: data[0].fund_name,
                    fieldOrder: columnLables[key]?.order,
                    nonEditable: isComparisonReport
                  })
                ];
          }

          // Each value column stacks its own value above its own error, so the
          // row must fit the tallest column — pairing the longest value with a
          // different column's longest error over-sizes it.
          const rows: GeographicalFocusRow[] = isMultiFunds
            ? isComparisonReport
              ? Object.values(funds)
                  .map(fund => [fund.previous_fund, fund])
                  .flat()
                  .map(fund => (fund as GeographicalFocusFund).fund_data['GEOGRAPHICAL_FOCUS'][0])
              : Object.values(funds).map(
                  fund => fund.fund_data['GEOGRAPHICAL_FOCUS'][0] as GeographicalFocusRow
                )
            : isComparisonReport
              ? [
                  funds[currentDataKey].previous_fund!.fund_data[
                    'GEOGRAPHICAL_FOCUS'
                  ][0] as GeographicalFocusRow,
                  data[0] as GeographicalFocusRow
                ]
              : [data[0] as GeographicalFocusRow];

          const valueCells: RowHeightCell[] = rows.map(row => {
            const fieldValue = row?.[fieldKey] as string | number | null | undefined;
            const isEmpty = fieldValue === null || fieldValue === undefined || fieldValue === '';
            return {
              value: isEmpty && isRequired ? 'This field is mandatory' : fieldValue || '',
              error: combineMessage(row?.errors?.[fieldKey]?.msg, row?.warning?.[fieldKey]?.msg)
            };
          });

          const rowHeight = getRowHeight({
            label: columnLables[key]?.name,
            labelWidth,
            cells: valueCells,
            valueWidth
          });
          return {
            rowId: isMultiFunds && isComparisonReport ? index + 2 : index + 1,
            height: rowHeight,
            cells: [
              {
                type: 'header',
                text: `${columnLables[key]?.order}`
              },
              {
                type: 'header',
                text: columnToLetter(ind + 1)
              },
              {
                type:
                  data[0]?.report_id &&
                  data[0]?.[key] !== null &&
                  schema.types[key]?.has_drill_down_feature
                    ? 'customHeader'
                    : 'header',
                text: columnLables[key].name || '',
                render: () => (
                  <CustomHeaderWithDrillDown
                    title={columnLables[key].name}
                    reportId={data[0]?.report_id}
                    fundId={data[0].fund_id}
                    columnName={key}
                    tableName="GEOGRAPHICAL_FOCUS"
                    tableId={data[0]?.id}
                  />
                ),
                style: {
                  'justify-content': 'flex-start',
                  'white-space': 'inherit'
                }
              },
              ...cells
            ]
          };
        })
      ];
    });
  }, [
    data,
    isStaticTemplate,
    isMultiFunds,
    funds,
    schema,
    isComparisonReport,
    contentWidth,
    requiredFields
  ]);

  return { tables, columns, onChange };
}
